import React from 'react';

import { Ipi, UpdateIpiData } from 'types/ipi.types';
import { IdentifierVerificationResponse, Link } from 'types/api.types';
import { useAppDispatch, useAppSelector } from 'app/store';
import { config } from 'config';
import { openURL } from 'app/utils/utils';
import { addAlertAction } from 'app/store/Alerts/slice';
import { useFetch, useResource } from 'app/utils/hooks';
import { initiateV2VerificationAction } from 'app/store/Mixpanel/slice';
import {
  addIpiManuallyEffect,
  fetchIpiValidationsEffect,
  fetchProSocietiesEffect,
  updateIpiEffect,
} from 'app/store/Profile/effects';
import {
  getAddIpiV2LinkSelector,
  getIpiValidations,
  getProSocieties,
  getProSocietiesListLink,
} from 'app/store/Profile/selectors';
import { SocietyOption } from 'app/components/Profile/Ids/types';
import { IpiForm } from './types';
import { toAddIpiData } from './utils';
import { IpiDialogComponent } from './IpiDialogComponent';

interface IpiDialogProps {
  open: boolean;
  ipi?: Ipi;
  onV3Verification: (data: SocietyOption) => void;
  onClose: (status?: number) => void;
}

export const IpiDialog: React.FC<IpiDialogProps> = ({
  open,
  ipi,
  onV3Verification,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const canFetchSocieties = Boolean(useAppSelector(getProSocietiesListLink));
  const addIpiV2Link = useAppSelector(getAddIpiV2LinkSelector);
  const { callAPI: addIpiV2 } = useFetch<
    { societyId: string },
    IdentifierVerificationResponse
  >();

  const [proSocieties, proSocietiesLoading] = useResource(
    fetchProSocietiesEffect,
    getProSocieties,
    {
      shouldFetch: canFetchSocieties && open,
    }
  );

  const [ipiValidations] = useResource(
    fetchIpiValidationsEffect,
    getIpiValidations,
    {
      shouldFetch: open && Boolean(proSocieties),
    }
  );

  const updateIpi = (formData: Partial<UpdateIpiData>, link?: Link) =>
    dispatch(updateIpiEffect({ formData, link })).unwrap();

  const handleAddIpiV2 = async (values: IpiForm) => {
    try {
      if (!values.society) return;
      const { response } = await addIpiV2(addIpiV2Link?.href || '', {
        societyId: values.society.id,
      });
      dispatch(initiateV2VerificationAction(values.society.title));
      openURL(response.identifierVerification._links.redirect.href, {
        isExternal: true,
      });
      if (config.isElectronBuild) {
        onClose();
      }
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
    }
  };

  const handleAddIpiV0 = async ({ isDefault, ...values }: Partial<IpiForm>) => {
    try {
      if (!values.society) return;
      const { status } = await dispatch(
        addIpiManuallyEffect({
          formData: toAddIpiData(values),
          societyTitle: values.society.title,
        })
      ).unwrap();
      onClose(status);
    } catch (e) {
      // proceed regardless
    }
  };

  return (
    <IpiDialogComponent
      open={open}
      ipiValidations={ipiValidations}
      societies={proSocieties || []}
      areSocietiesLoading={proSocietiesLoading}
      ipi={ipi}
      handleAddIpiV0={handleAddIpiV0}
      handleAddIpiV2={handleAddIpiV2}
      handleAddIpiV3={onV3Verification}
      updateIpi={updateIpi}
      onClose={onClose}
    />
  );
};
